'use client';

import Utils from '@/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { BiCheckCircle, BiSolidCheckCircle, BiXCircle } from 'react-icons/bi';
import { HiOutlineShieldCheck } from 'react-icons/hi';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

type ConsentType = {
  ad_storage: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  personalization_storage: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
};

type CheckBoxesType = {
  necessary: boolean;
  analytics: boolean;
  preferences: boolean;
  marketing: boolean;
};

type Props = { locale: string };

const ConsentManager = ({ locale }: Props) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const [isConsentSet, setIsConsentSet] = useState(false);

  const [isUserInteracted, setIsUserInteracted] = useState(false);
  const defaultTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const consentRef = useRef<HTMLDivElement | null>(null);

  const [checkBoxes, setCheckBoxes] = useState<CheckBoxesType>({
    necessary: true,
    analytics: true,
    preferences: true,
    marketing: false,
  });

  const handlePersistConsents = useCallback(
    (consent: ConsentType) => {
      if (typeof Storage !== 'undefined' && window.localStorage && typeof localStorage !== 'undefined') {
        // Modifier pour éviter de réinitialiser si un consentement existe déjà
        let currentConsent = localStorage.getItem('consentMode');

        if (!currentConsent) {
          window.gtag('consent', 'update', consent);
          localStorage.setItem('consentMode', JSON.stringify(consent));

          if (searchParams) {
            Utils.utmManager(searchParams);
          }

          document.body.classList.remove('consent-manager-open');
          setOpen(false);
          setIsConsentSet(true);
        }
      }
    },
    [searchParams]
  );

  const handleUserInteraction = useCallback(() => {
    if (!isUserInteracted) {
      setIsUserInteracted(true);

      // Annuler le timeout par défaut si l'utilisateur interagit
      if (defaultTimeoutRef.current) {
        clearTimeout(defaultTimeoutRef.current);
      }
    }
  }, [isUserInteracted]);

  const handleConsents = useCallback(
    (type: 'all' | 'selection' | 'none', currentCheckBoxes: CheckBoxesType) => {
      const enumConsent: { all: ConsentType; selection: ConsentType; none: ConsentType } = {
        all: {
          ad_storage: 'granted',
          analytics_storage: 'granted',
          personalization_storage: 'granted',
          functionality_storage: 'granted',
          security_storage: 'granted',
        },
        selection: {
          ad_storage: currentCheckBoxes.marketing ? 'granted' : 'denied',
          analytics_storage: currentCheckBoxes.analytics ? 'granted' : 'denied',
          personalization_storage: currentCheckBoxes.preferences ? 'granted' : 'denied',
          functionality_storage: 'granted',
          security_storage: 'granted',
        },
        none: {
          ad_storage: 'denied',
          analytics_storage: 'denied',
          personalization_storage: 'denied',
          functionality_storage: 'granted',
          security_storage: 'granted',
        },
      };

      handlePersistConsents(enumConsent[type]);
    },
    [handlePersistConsents]
  );

  useEffect(() => {
    // Vérifier si un consentement existe déjà
    const currentConsent = localStorage.getItem('consentMode');

    if (currentConsent) {
      setIsConsentSet(true);
      return;
    }

    // Ne pas afficher sur les pages de chat
    if (pathname?.includes('/chat/')) {
      return;
    }

    // Délai avant d'afficher la modal
    const timer = setTimeout(() => {
      setOpen(true);
      document.body.classList.add('consent-manager-open');
    }, 5000);

    // Nouveau timeout pour les valeurs par défaut
    defaultTimeoutRef.current = setTimeout(() => {
      if (!isUserInteracted) {
        // Si aucune interaction après 10 secondes, accepter par défaut
        handleConsents('all', {
          necessary: true,
          analytics: true,
          preferences: true,
          marketing: false,
        });
      }
    }, 10000);

    return () => {
      clearTimeout(timer);
      if (defaultTimeoutRef.current) {
        clearTimeout(defaultTimeoutRef.current);
      }
      document.body.classList.remove('consent-manager-open');
    };
  }, [pathname, isUserInteracted, handleConsents]);

  // Empêcher la fermeture automatique lors des changements de checkboxes
  const handleCheckboxChange = (key: keyof CheckBoxesType, value: boolean) => {
    setCheckBoxes((prev) => ({ ...prev, [key]: value }));
  };

  // Ne pas afficher si le consentement est déjà donné ou sur une page de chat
  if (isConsentSet || pathname?.includes('/chat/') || !open) {
    return null;
  }

  return (
    <div
      className="tw-fixed tw-bottom-4 tw-left-1 tw-z-[1000] tw-flex tw-justify-center tw-w-full tw-max-w-xl tw-p-4 tw-mx-auto"
      ref={consentRef}
      onClick={handleUserInteraction}
      onMouseMove={handleUserInteraction}
      onKeyDown={handleUserInteraction}
    >
      <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-border tw-border-gray-200 tw-p-4 sm:tw-p-6 tw-overflow-hidden tw-relative tw-w-full tw-max-w-md">
        {/* Background gradient */}
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-50 tw-via-white tw-to-purple-50 tw-opacity-50 tw--z-10 tw-blur-2xl"></div>

        {/* Close button */}
        <button
          onClick={() => handleConsents('all', checkBoxes)}
          className="tw-absolute tw-top-2 sm:tw-top-4 tw-right-2 sm:tw-right-4 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
        >
          <BiXCircle className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6" />
        </button>

        {/* Icon */}
        <div className="tw-flex tw-justify-center tw-mb-2">
          <Image
            priority
            quality={100}
            src={'https://cdn.1cdn.app/application/PIL/24050806320483_American_Cookie_-_1372x1347.png'}
            alt="cookie"
            title="cookie"
            width={40}
            height={40}
          />
        </div>

        {/* Title & Description */}
        <h2 className="tw-text-center tw-text-sm tw-font-bold tw-text-gray-900 tw-mb-2 sm:tw-mb-3">
          Cookie Preferences
        </h2>
        <p className="tw-text-center tw-text-gray-600 tw-mb-4 sm:tw-mb-6 tw-text-xs sm:tw-text-sm tw-px-2 sm:tw-px-4">
          We use cookies to enhance your experience and site usage. You can customize your preferences below.
        </p>

        {/* Checkboxes */}
        <div className="tw-space-y-2 sm:tw-space-y-3 tw-mb-4 sm:tw-mb-6">
          {[
            {
              id: 'necessary',
              label: 'Necessary',
              description: 'Essential cookies for site functionality',
              disabled: true,
              checked: checkBoxes.necessary,
              onChange: () => handleUserInteraction(),
            },
            {
              id: 'analytics',
              label: 'Analytics',
              description: 'Usage and performance insights',
              checked: checkBoxes.analytics,
              onChange: (checked: boolean) => {
                handleCheckboxChange('analytics', checked);
                handleUserInteraction();
              },
            },
            {
              id: 'preferences',
              label: 'Preferences',
              description: 'Personalization and user experience',
              checked: checkBoxes.preferences,
              onChange: (checked: boolean) => {
                handleCheckboxChange('preferences', checked);
                handleUserInteraction();
              },
            },
            {
              id: 'marketing',
              label: 'Marketing',
              description: 'Targeted advertising and promotions',
              checked: checkBoxes.marketing,
              onChange: (checked: boolean) => {
                handleCheckboxChange('marketing', checked);
                handleUserInteraction();
              },
            },
          ].map((item) => (
            <div
              key={item.id}
              className={`tw-flex tw-items-center tw-justify-between tw-p-2 sm:tw-p-3 tw-rounded-xl tw-border ${
                item.checked ? 'tw-border-blue-200 tw-bg-blue-50' : 'tw-border-gray-200 tw-bg-white'
              }`}
              onClick={handleUserInteraction}
            >
              <div className="tw-flex tw-items-center tw-gap-2 sm:tw-gap-3">
                <input
                  type="checkbox"
                  id={item.id}
                  checked={item.checked}
                  disabled={item.disabled}
                  onChange={!item.disabled ? (e) => item.onChange?.(e.target.checked) : undefined}
                  className="tw-hidden peer"
                />
                <label
                  htmlFor={item.id}
                  className={`tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5 tw-rounded-md tw-border tw-flex tw-items-center tw-justify-center tw-cursor-pointer 
                  ${
                    item.disabled
                      ? 'tw-bg-gray-200 tw-border-gray-300'
                      : `tw-border-gray-300 peer-checked:tw-bg-blue-600 peer-checked:tw-border-blue-600 
                       hover:tw-border-blue-500 tw-transition-colors`
                  }`}
                >
                  {item.checked && !item.disabled && (
                    <BiSolidCheckCircle className="tw-w-3 tw-h-3 sm:tw-w-4 sm:tw-h-4 tw-text-blue-400" />
                  )}
                </label>

                <div>
                  <div
                    className={`tw-font-semibold tw-text-xs sm:tw-text-sm ${
                      item.checked ? 'tw-text-blue-900' : 'tw-text-gray-900'
                    }`}
                  >
                    {item.label}
                  </div>
                  <div className="tw-text-[10px] sm:tw-text-xs tw-text-gray-500">{item.description}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-3">
          <button
            onClick={() => {
              handleUserInteraction();
              handleConsents('all', checkBoxes);
            }}
            className="tw-text-xs sm:tw-text-sm tw-w-full tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-py-2 sm:tw-py-3 tw-rounded-xl tw-font-semibold tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-2"
          >
            <BiCheckCircle className="tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5" />
            Accept All
          </button>

          <button
            onClick={() => {
              handleUserInteraction();
              handleConsents('selection', checkBoxes);
            }}
            className="tw-text-xs sm:tw-text-sm tw-w-full tw-bg-blue-50 hover:tw-bg-blue-100 tw-text-blue-700 tw-py-2 sm:tw-py-3 tw-rounded-xl tw-font-semibold tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-2"
          >
            <HiOutlineShieldCheck className="tw-w-4 tw-h-4 sm:tw-w-5 sm:tw-h-5" />
            Accept Selection
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentManager;
